import { Spinner } from '@frontend/components/common';
import { CommonMessage, getCorrectTranslation } from '@frontend/lang';
import { useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import PlusMinusNumberInput from '../../../components/plus-minus-number-input/plus-minus-number-input.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import { useConsumeKit } from './consume-kit.controller';

export const ConsumeKit = () => {
    const viewProps = useConsumeKit();
    const [kitItemsExpanded, setKitItemsExpanded] = useState<boolean>(false);

    if (viewProps.isLoading) {
        return (
            <WizardScreenContainer
                footer={{ onBack: viewProps.navigateBack }}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
                <div className='flex h-full w-full items-center justify-center py-12'>
                    <Spinner />
                </div>
            </WizardScreenContainer>
        );
    }

    if (!viewProps.kit) {
        return (
            <WizardScreenContainer
                footer={{ onBack: viewProps.navigateBack }}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
                <div className='flex h-full w-full items-center justify-center py-12'>
                    <p>
                        <FormattedMessage
                            defaultMessage='No kit selected'
                            id='wizard.consume-kit.kit-not-found'
                        />
                    </p>
                </div>
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            footer={{
                onBack: viewProps.navigateBack,
                onSubmit: viewProps.confirmKitConsumption,
                submitLabel: CommonMessage.BUTTONS.CONTINUE,
                submitDisabled: !viewProps.canConsumeKit
            }}
            header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
            <div className='flex w-full flex-col items-start gap-6'>
                <div className='flex w-full flex-col items-start gap-3'>
                    <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                        <p>{CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT}</p>
                    </div>
                    <ProductCard
                        product={viewProps.kit}
                        variant='attributes'
                    />
                    {viewProps.kit.user_info && (
                        <div className='bg-background-surface border-border-default inline-flex w-full items-start justify-start gap-3 self-stretch rounded-xl border p-3'>
                            {getCorrectTranslation(viewProps.locale, viewProps.kit, 'user_info')}
                        </div>
                    )}
                </div>

                <div className='flex w-full flex-col items-start gap-3'>
                    <button
                        type='button'
                        className='text-foreground-default flex w-full items-center justify-between gap-3 text-base font-medium'
                        onClick={() => setKitItemsExpanded((expanded) => !expanded)}>
                        <span>
                            <FormattedMessage
                                id='wizard.consume-kit.items'
                                defaultMessage='Kit items'
                                description='Section title for the list of products contained in the selected kit'
                            />
                        </span>
                        {kitItemsExpanded ? <LuChevronUp className='size-5 shrink-0' /> : <LuChevronDown className='size-5 shrink-0' />}
                    </button>
                    {viewProps.children.length === 0 ? (
                        <div className='text-foreground-muted py-4 text-sm'>
                            <FormattedMessage
                                id='wizard.consume-kit.no-items'
                                defaultMessage='This kit has no items'
                            />
                        </div>
                    ) : kitItemsExpanded ? (
                        <div className='flex w-full flex-col gap-2'>
                            {viewProps.itemAvailabilities.map((item) => (
                                <div
                                    key={item.product.id}
                                    className='relative w-full'>
                                    <div className='flex w-full flex-col gap-2'>
                                        <ProductCard
                                            product={item.product}
                                            variant='attributes'
                                            styles={{
                                                container: item.isFullyAvailable ? undefined : 'opacity-50'
                                            }}
                                        />
                                        {item.product.user_info && (
                                            <div
                                                className={`bg-background-surface border-border-default inline-flex w-full items-start justify-start gap-3 self-stretch rounded-xl border p-3 ${item.isFullyAvailable ? '' : 'opacity-50'}`}>
                                                {getCorrectTranslation(viewProps.locale, item.product, 'user_info')}
                                            </div>
                                        )}
                                    </div>
                                    <div className='bg-brand-primary text-background-surface absolute top-0 right-0 z-10 flex h-8 min-w-8 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full px-2 text-sm font-semibold'>
                                        {item.required}x
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                {viewProps.equipmentChildren.length + viewProps.consumableChildren.length > 0 && viewProps.maxAmount > 0 && (
                    <div className='flex w-full flex-col items-start gap-3'>
                        <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                            <p>{CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT}</p>
                        </div>
                        <PlusMinusNumberInput
                            min={1}
                            max={viewProps.maxAmount}
                            value={viewProps.amount}
                            onChange={viewProps.changeAmount}
                        />
                    </div>
                )}

                {viewProps.missingProducts.length > 0 && (
                    <div className='text-foreground-muted text-sm'>
                        <FormattedMessage
                            id='wizard.consume-kit.missing-equipment'
                            defaultMessage='Some kit items are not available at this distriSPOT.'
                        />
                    </div>
                )}

                {viewProps.hasWorkflowConfigurationError && (
                    <div className='text-status-danger text-sm font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.consume-kit.workflow-configuration-error'
                            description='Error shown when the Vending or Lending workflow required to fulfill a kit is not configured'
                            defaultMessage='This kit cannot be fulfilled because one or more workflows are not configured. Please contact support.'
                        />
                    </div>
                )}
            </div>
        </WizardScreenContainer>
    );
};
